/* eslint-disable react-hooks/exhaustive-deps */

import React, { createContext, useContext, useState, useEffect } from 'react';

import { QuoteContext } from '../contexts/quoteContext';
import { ConfirmationData } from '../components/common/summary-common/confirmationData';
import { defaultCoverageInformation, updateCoverageInformation } from '../components/application/INT/templates/confirmation/defaultCoverageInformation';
import { defaultPersonalInformationOfTheInsured, updatePersonalInformationOfTheInsured } from '../components/application/INT/templates/confirmation/defaultPersonalInformationOfTheInsured';
import { defaultPolicyOwnerInformation, updatePolicyOwnerInformation } from '../components/application/INT/templates/confirmation/defaultPolicyOwnerInformation';
import { defaultPolicyPurposeInformation, updatePolicyPurposeInformation } from '../components/application/INT/templates/confirmation/defaultPolicyPurposeInformation';
import { defaultOccupationInformation, updateOccupationInformation } from '../components/application/INT/templates/confirmation/defaultOccupationInformation';
import { defaultIncomeInformation, updateIncomeInformation } from '../components/application/INT/templates/confirmation/defaultIncomeInformation';
import { defaultRiskyActivitiesInformation, updateRiskyActivitiesInformation } from '../components/application/INT/templates/confirmation/defaultRiskyActivitiesInformation';
import { defaultInsuranceInformation, updateInsuranceInformation } from '../components/application/INT/templates/confirmation/defaultInsuranceInformation';
import { defaultSubstanceInformation, updateSubstanceInformation } from '../components/application/INT/templates/confirmation/defaultSubstanceInformation';
import { defaultFinancialInformation, updateFinancialInformation } from '../components/application/INT/templates/confirmation/defaultFinancialInformation';
import { defaultReferralInformation, updateReferralInformation } from '../components/application/INT/templates/confirmation/defaultReferralInformation';
import { defaultBeneficiaryInformation, updateBeneficiaryInformation } from '../components/application/INT/templates/confirmation/defaultBeneficiaryInformation';
import { defaultPoliticallyExposedPersonInformation, updatePoliticallyExposedPersonInformation } from '../components/application/INT/templates/confirmation/defaultPoliticallyExposedPersonInformation';
import { defaultPhysicalInformation, updatePhysicalInformation } from '../components/application/INT/templates/confirmation/defaultPhysicalInformation';
import { defaultFamilyMedicalHistoryInformation, updateFamilyMedicalHistoryInformation } from '../components/application/INT/templates/confirmation/defaultFamilyMedicalHistoryInformation';
import { defaultMedicalInformation, updateMedicalInformation } from '../components/application/INT/templates/confirmation/defaultMedicalInformation';
import { getApplicationSummary } from '../api/applicationService';
import { defaultAdditionalInsured, updateAdditionalInsured } from '../components/application/INT/templates/confirmation/defaultAdditionalInsured';
import { useNavigate } from 'react-router-dom';
import { createInitialPaymentSession, createPaymentSession, PaymentSessionResponse } from '../api/paymentService';
import { generarUuid } from '../utils/utils';
import { getDependents } from '../components/application/INT/builder/pages';
import { ApplicationSummary, findValue } from '../components/common/summary-common/applicationSummary';

// Definir el tipo Dependent directamente en lugar de importarlo
interface Dependent {
    id: string;
    name: string;
    relationship: string;
    insuredEqualOwner?: boolean;
}

interface ConfirmationContextType {
    isLoading: boolean;
    setIsLoading: (value: boolean) => void;
    coverageInformation: Map<string, ConfirmationData>;
    setCoverageInformation: (values: Map<string, ConfirmationData>) => void;
    personalInformationOfTheInsured: Map<string, ConfirmationData>;
    setPersonalInformationOfTheInsured: (values: Map<string, ConfirmationData>) => void;
    policyOwnerInformation: Map<string, ConfirmationData>;
    setPolicyOwnerInformation: (values: Map<string, ConfirmationData>) => void;
    policyPurposeInformation: Map<string, ConfirmationData>;
    setPolicyPurposeInformation: (values: Map<string, ConfirmationData>) => void;
    occupationInformation: Map<string, ConfirmationData>;
    setOccupationInformation: (values: Map<string, ConfirmationData>) => void;
    incomeInformation: Map<string, ConfirmationData>;
    setIncomeInformation: (values: Map<string, ConfirmationData>) => void;
    politicallyExposedPersonInformation: Map<string, ConfirmationData>;
    setPoliticallyExposedPersonInformation: (values: Map<string, ConfirmationData>) => void;
    riskyActivitiesInformation: Map<string, ConfirmationData>;
    setRiskyActivitiesInformation: (values: Map<string, ConfirmationData>) => void;
    physicalInformation: Map<string, ConfirmationData>;
    setPhysicalInformation: (values: Map<string, ConfirmationData>) => void;
    medicalInformation: Map<string, ConfirmationData>;
    setMedicalInformation: (values: Map<string, ConfirmationData>) => void;
    familyMedicalHistoryInformation: Map<string, ConfirmationData>;
    setFamilyMedicalHistoryInformation: (values: Map<string, ConfirmationData>) => void;
    insuranceInformation: Map<string, ConfirmationData>;
    setInsuranceInformation: (values: Map<string, ConfirmationData>) => void;
    substanceInformation: Map<string, ConfirmationData>;
    setSubstanceInformation: (values: Map<string, ConfirmationData>) => void;
    financialInformation: Map<string, ConfirmationData>;
    setFinancialInformation: (values: Map<string, ConfirmationData>) => void;
    beneficiaryInformation: Map<string, ConfirmationData>;
    setBeneficiaryInformation: (values: Map<string, ConfirmationData>) => void;
    referralInformation: Map<string, ConfirmationData>;
    setReferralInformation: (values: Map<string, ConfirmationData>) => void;
    additionalInsured: Map<string, ConfirmationData>;
    setAdditionalInsured: (values: Map<string, ConfirmationData>) => void;
    email: string;
    setEmail: (value: string) => void;
    backToApplication: () => void;
    backToQuoter: () => void;
    getPageIdByTemplateId: (templateId: string) => number;
    getPaymentSession: () => Promise<PaymentSessionResponse>;
    dependents: Dependent[];
    dependentsMedicalInformation: Map<string, Map<string, ConfirmationData>>;
}

const getPageIdByTemplateId = (templateId: string, applicationSummary: ApplicationSummary[]): number => {
    const page = applicationSummary.find((page) => page.template_page_id === templateId);
    return page?.id || 123;
};

const ConfirmationContext = createContext<ConfirmationContextType>({} as ConfirmationContextType);

export const ConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [applicationSummary, setApplicationSummary] = useState<ApplicationSummary[]>([]);
    const { selected, lastQuote, quoteID } = useContext(QuoteContext);
    const [coverageInformation, setCoverageInformation] = useState<Map<string, ConfirmationData>>(defaultCoverageInformation);
    const [personalInformationOfTheInsured, setPersonalInformationOfTheInsured] = useState<Map<string, ConfirmationData>>(defaultPersonalInformationOfTheInsured);
    const [policyOwnerInformation, setPolicyOwnerInformation] = useState<Map<string, ConfirmationData>>(defaultPolicyOwnerInformation);
    const [policyPurposeInformation, setPolicyPurposeInformation] = useState<Map<string, ConfirmationData>>(defaultPolicyPurposeInformation);
    const [occupationInformation, setOccupationInformation] = useState<Map<string, ConfirmationData>>(defaultOccupationInformation);
    const [incomeInformation, setIncomeInformation] = useState<Map<string, ConfirmationData>>(defaultIncomeInformation);
    const [politicallyExposedPersonInformation, setPoliticallyExposedPersonInformation] = useState<Map<string, ConfirmationData>>(defaultPoliticallyExposedPersonInformation);
    const [riskyActivitiesInformation, setRiskyActivitiesInformation] = useState<Map<string, ConfirmationData>>(defaultRiskyActivitiesInformation);
    const [physicalInformation, setPhysicalInformation] = useState<Map<string, ConfirmationData>>(defaultPhysicalInformation);
    const [medicalInformation, setMedicalInformation] = useState<Map<string, ConfirmationData>>(defaultMedicalInformation);
    const [familyMedicalHistoryInformation, setFamilyMedicalHistoryInformation] = useState<Map<string, ConfirmationData>>(defaultFamilyMedicalHistoryInformation);
    const [insuranceInformation, setInsuranceInformation] = useState<Map<string, ConfirmationData>>(defaultInsuranceInformation);
    const [substanceInformation, setSubstanceInformation] = useState<Map<string, ConfirmationData>>(defaultSubstanceInformation);
    const [financialInformation, setFinancialInformation] = useState<Map<string, ConfirmationData>>(defaultFinancialInformation);
    const [beneficiaryInformation, setBeneficiaryInformation] = useState<Map<string, ConfirmationData>>(defaultBeneficiaryInformation);
    const [referralInformation, setReferralInformation] = useState<Map<string, ConfirmationData>>(defaultReferralInformation);
    const [additionalInsured, setAdditionalInsured] = useState<Map<string, ConfirmationData>>(defaultAdditionalInsured);
    const [email, setEmail] = useState<string>('');
    const [typePerson, setTypePerson] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [state, setState] = useState<string>('');
    const [country, setCountry] = useState<string>('');
    const [zipCode, setZipCode] = useState<string>('');
    const [dependents, setDependents] = useState<Dependent[]>([]);
    const [dependentsMedicalInformation, setDependentsMedicalInformation] = useState<Map<string, Map<string, ConfirmationData>>>(new Map());
    const navigate = useNavigate();

    const backToApplication = () => {
        navigate(`/application?quoteID=${quoteID}`);
    }

    const backToQuoter = () => {
        navigate(`/quote/?quoteID=${quoteID}`);
    }

    useEffect(() => {
        if (applicationSummary?.length > 0 && JSON.stringify([...coverageInformation]) !== JSON.stringify([...defaultCoverageInformation])) {
            setIsLoading(false);
        }
    }, [coverageInformation, applicationSummary]);

    useEffect(() => {
        if (selected && lastQuote) {
            setCoverageInformation(updateCoverageInformation(selected, lastQuote, coverageInformation));
        }
    }, [selected, lastQuote]);

    useEffect(() => {
        if (quoteID) {
            fetchApplicationSummary(quoteID);
        }
    }, [quoteID]);

    useEffect(() => {
        const loadApplicationData = async () => {
            if (applicationSummary) {
                const pagePersonalInformationOfTheInsured = applicationSummary.find((page) => page.template_page_id === '0d7e941e-7c31-41ee-95f4-622c54525a72');
                setPersonalInformationOfTheInsured(updatePersonalInformationOfTheInsured(pagePersonalInformationOfTheInsured?.json, defaultPersonalInformationOfTheInsured));

                const pagePolicyOwnerInformation = applicationSummary.find((page) => page.template_page_id === 'd487348e-4062-4daa-92fc-854869a2fc81');
                setPolicyOwnerInformation(updatePolicyOwnerInformation(pagePolicyOwnerInformation?.json, defaultPolicyOwnerInformation));
                if (pagePolicyOwnerInformation) {
                    setTypePerson(findValue(pagePolicyOwnerInformation?.json, '12e08772-9181-4b03-92a2-2b8487eaac4d', '18e342e2-2e26-4522-b54a-5961eda04344', "c25db4af-ac2f-4539-8490-735c10f388df"));
                    setEmail(findValue(pagePolicyOwnerInformation?.json, '12e08772-9181-4b03-92a2-2b8487eaac4d', '18e342e2-2e26-4522-b54a-5961eda04344', "a1c1f62e-326b-46f0-bb7d-33d436ca267d"));
                    setFirstName(findValue(pagePolicyOwnerInformation?.json, '12e08772-9181-4b03-92a2-2b8487eaac4d', '18e342e2-2e26-4522-b54a-5961eda04344', "99437cde-338a-44df-bb0b-dd4d6af67935"));
                    setLastName(findValue(pagePolicyOwnerInformation?.json, '12e08772-9181-4b03-92a2-2b8487eaac4d', '18e342e2-2e26-4522-b54a-5961eda04344', "99437cde-338a-44df-bb0b-dd4d6af67935"));
                    setPhone(findValue(pagePolicyOwnerInformation?.json, '12e08772-9181-4b03-92a2-2b8487eaac4d', '18e342e2-2e26-4522-b54a-5961eda04344', "103a15f4-0d02-496a-bf0f-994ee9e4b945"));
                    setAddress(findValue(pagePolicyOwnerInformation?.json, '8abc8b37-e0b3-43bf-a8fe-1bb74d71a2a2', '58db12a8-16eb-4399-a7b7-a83be0ec4d95', "a75a9b18-e3e9-4631-b638-ea28bdf76190"));
                    setCity(findValue(pagePolicyOwnerInformation?.json, '8abc8b37-e0b3-43bf-a8fe-1bb74d71a2a2', '58db12a8-16eb-4399-a7b7-a83be0ec4d95', "34379aab-6f0f-4c7d-8329-36eb870526c0"));
                    setState(findValue(pagePolicyOwnerInformation?.json, '8abc8b37-e0b3-43bf-a8fe-1bb74d71a2a2', '58db12a8-16eb-4399-a7b7-a83be0ec4d95', "7c4e50cc-4488-4bf6-9388-1f1485fb473e"));
                    setCountry(findValue(pagePolicyOwnerInformation?.json, '8abc8b37-e0b3-43bf-a8fe-1bb74d71a2a2', '58db12a8-16eb-4399-a7b7-a83be0ec4d95', "ce33e4e7-1939-4a45-84b4-a5788c0c6aff"));
                    setZipCode(findValue(pagePolicyOwnerInformation?.json, '8abc8b37-e0b3-43bf-a8fe-1bb74d71a2a2', '58db12a8-16eb-4399-a7b7-a83be0ec4d95', "cd229724-cb09-4e70-9759-3fcd22e70723"));

                } else {
                    setTypePerson(findValue(pagePersonalInformationOfTheInsured?.json, '872a5c66-cd6e-4fbc-baa8-0b5ff69f446c', '3e75c9bf-b2aa-4e43-896a-b3ed637de929', "c25db4af-ac2f-4539-8490-735c10f388df"));
                    setEmail(findValue(pagePersonalInformationOfTheInsured?.json, '872a5c66-cd6e-4fbc-baa8-0b5ff69f446c', '3e75c9bf-b2aa-4e43-896a-b3ed637de929', "a1c1f62e-326b-46f0-bb7d-33d436ca267d"));
                    setFirstName(findValue(pagePersonalInformationOfTheInsured?.json, '872a5c66-cd6e-4fbc-baa8-0b5ff69f446c', '3e75c9bf-b2aa-4e43-896a-b3ed637de929', "773e8c82-3c69-4745-8520-43ac32b1b030"));
                    setLastName(findValue(pagePersonalInformationOfTheInsured?.json, '872a5c66-cd6e-4fbc-baa8-0b5ff69f446c', '3e75c9bf-b2aa-4e43-896a-b3ed637de929', "84f4b5e2-a240-4405-839b-a35924df2fa8"));
                    setPhone(findValue(pagePersonalInformationOfTheInsured?.json, '872a5c66-cd6e-4fbc-baa8-0b5ff69f446c', '3e75c9bf-b2aa-4e43-896a-b3ed637de929', "103a15f4-0d02-496a-bf0f-994ee9e4b945"));
                    setAddress(findValue(pagePersonalInformationOfTheInsured?.json, '54d6888c-f0ba-4a7e-a463-5460ecf922a4', 'c6b9ce70-2cdb-461a-b263-6b4d04f6b705', "a75a9b18-e3e9-4631-b638-ea28bdf76190"));
                    setCity(findValue(pagePersonalInformationOfTheInsured?.json, '54d6888c-f0ba-4a7e-a463-5460ecf922a4', 'c6b9ce70-2cdb-461a-b263-6b4d04f6b705', "34379aab-6f0f-4c7d-8329-36eb870526c0"));
                    setState(findValue(pagePersonalInformationOfTheInsured?.json, '54d6888c-f0ba-4a7e-a463-5460ecf922a4', 'c6b9ce70-2cdb-461a-b263-6b4d04f6b705', "7c4e50cc-4488-4bf6-9388-1f1485fb473e"));
                    setCountry(findValue(pagePersonalInformationOfTheInsured?.json, '54d6888c-f0ba-4a7e-a463-5460ecf922a4', 'c6b9ce70-2cdb-461a-b263-6b4d04f6b705', "ce33e4e7-1939-4a45-84b4-a5788c0c6aff"));
                    setZipCode(findValue(pagePersonalInformationOfTheInsured?.json, '54d6888c-f0ba-4a7e-a463-5460ecf922a4', 'c6b9ce70-2cdb-461a-b263-6b4d04f6b705', "cd229724-cb09-4e70-9759-3fcd22e70723"));
                }

                const pagePolicyPurposeInformation = applicationSummary.find((page) => page.template_page_id === "e67fe061-ef65-4e06-86f6-4498b3d9ea69");
                setPolicyPurposeInformation(updatePolicyPurposeInformation(pagePolicyPurposeInformation?.json, defaultPolicyPurposeInformation));

                const pageOccupationInformation = applicationSummary.find((page) => page.template_page_id === "8cfdcedb-484c-4633-bcc2-17fe2447a5c8");
                setOccupationInformation(updateOccupationInformation(pageOccupationInformation?.json, defaultOccupationInformation));

                const pageIncomeInformation = applicationSummary.find((page) => page.template_page_id === "4ad0e8b3-a435-4990-92db-7e7e61cb954c");
                setIncomeInformation(updateIncomeInformation(pageIncomeInformation?.json, defaultIncomeInformation));

                const riskyActivitiesPageIds = [
                    "0ac905cb-800c-4db8-ba9e-9a7874cbeb34",
                    "69e6100c-1a76-4330-b750-cb35c5472d36",
                    "bfe6c586-e763-44aa-ae04-3e3000cb5380",
                    "6d533b7f-5698-4eaa-a9ce-a7c47f320a89",
                    "35a04d2e-a8c0-4313-be5b-e531c219f9b1",
                    "aa074f57-9cec-48a7-a61e-4d12f0a04deb",
                    "d10fc68c-1ee8-435d-a136-70916b6e8907",
                    "90ab8a8e-22b0-42a6-a0d2-e02db8c02f00",
                    "8f55eb85-6324-463f-b035-a52457a05c8c",
                    "89e56224-de88-466e-a3a2-11c26be51995"

                ];
                const riskyActivitiesPages = applicationSummary.filter(page => riskyActivitiesPageIds.includes(page.template_page_id));
                let ri = defaultRiskyActivitiesInformation;
                if (riskyActivitiesPages.length > 0) {
                    // Combinar todas las secciones en el primer elemento
                    const combinedJson = {
                        ...riskyActivitiesPages[0].json,
                        sections: riskyActivitiesPages.flatMap(page => page.json?.sections || [])
                    };
                    ri = updateRiskyActivitiesInformation(combinedJson, ri);
                }
                setRiskyActivitiesInformation(ri);

                const medicalPageIds = [
                    "5d277b8b-6318-4f43-8b22-5bd9f69d1658",
                    "de057444-1ac0-43da-8cff-0b21d2f63ef6",
                    "ba08302f-5328-44bf-85bd-f377974da10b",
                    "2e9ec1a1-054e-4a2f-9f43-cd23f9fb272a",
                    "544a1eb6-b33b-4ba2-8854-62d85dd716de",
                    "48eb7343-efb5-447f-9883-7aa0661e5c83",
                    "af7cc509-d688-4316-9ee3-320f0322ad05",
                    "f0b7cc30-6245-4293-96f0-2fe0bff87440",
                    "45fbdd22-a478-480f-9e42-fd1b0f213306",
                    "de057444-1ac0-43da-8cff-0b21d2f63ef6",
                    "c3ba699c-f173-4f13-84b7-59cdd9d2e579",
                    "2435169b-5fbe-438c-8e1c-6bea8f84d776",
                    "99fc15df-21a9-454b-8c98-a1de719c4362",
                    "59ba071c-9696-4e03-8c6e-de1bd3abd809",
                    "c117f0fe-c687-4cf1-9428-8a672d36b434",
                    "48eb7343-efb5-447f-9883-7aa0661e5c83",
                    "449763f5-7a20-4eed-a9c0-1679d19f1005",
                    "f75336d7-cd43-4a1c-95d3-136adb13f2c8",
                    "c4b3268a-5fba-41fe-91e2-b38cd184f299",
                    "2258d150-3cd5-4300-858e-decc15f48e1b",
                    "0bb664c3-720a-49ea-aa07-3d307357a930",
                    "4a2866c6-d1d6-4d90-9dc8-728c640e3ba6",
                    "33a0e6e5-c529-4cc7-ac12-20e3b480b93a",
                    "ce92985c-c827-4437-b4c6-8fbb130ddbcd",
                    "bfdabbae-399a-47d5-9f4d-f22a34c6c719",
                    "1271b2a1-8f78-482f-8824-e8481a71cf56",
                    "5a4c32e0-c4a8-4111-879d-5e97e788305b",
                    "ca971a79-7736-4ef5-8903-f92ddcc6b210",
                    "a1b5f826-0921-4cff-9511-5574313b68a3",
                    "4df604ae-4da5-403d-889f-5ddf56443e66",
                    "133b3a35-1c82-4b66-aa24-956f01062da4",
                    "6105330c-5aae-4a7b-b597-aa84f7e573b5",
                ];
                const medicalPages = applicationSummary.filter(page => medicalPageIds.includes(page.template_page_id));
                let mi = defaultMedicalInformation;
                if (medicalPages.length > 0) {
                    // Combinar todas las secciones en el primer elemento
                    const combinedJson = {
                        ...medicalPages[0].json,
                        sections: medicalPages.flatMap(page => page.json?.sections || [])
                    };
                    mi = updateMedicalInformation(combinedJson, mi);
                }

                setMedicalInformation(mi);

                const pageFamilyMedicalHistoryInformation = applicationSummary.find((page) => page.template_page_id === "6819c081-02fa-4763-9ce5-65d14ca50b9e");
                setFamilyMedicalHistoryInformation(updateFamilyMedicalHistoryInformation(pageFamilyMedicalHistoryInformation?.json, defaultFamilyMedicalHistoryInformation));

                const pageInsuredInformation = applicationSummary.find((page) => page.template_page_id === "5bb55af3-e811-407c-87c1-711758b6066a");
                setInsuranceInformation(updateInsuranceInformation(pageInsuredInformation?.json, defaultInsuranceInformation));

                const pageSubstanceInformation = applicationSummary.find((page) => page.template_page_id === "1e13cae4-72e6-4450-9380-68aa61c1dada");
                setSubstanceInformation(updateSubstanceInformation(pageSubstanceInformation?.json, defaultSubstanceInformation));

                const financialPageIds = [
                    "fc943429-3caf-4962-b6df-f46bb8058524",
                    "4ba68098-7678-470e-b299-b0fcd1e0ec83"
                ];
                const financialPages = applicationSummary.filter(page => financialPageIds.includes(page.template_page_id));
                let fi = defaultFinancialInformation;
                if (financialPages.length > 0) {
                    // Combinar todas las secciones en el primer elemento
                    const combinedJson = {
                        ...financialPages[0].json,
                        sections: financialPages.flatMap(page => page.json?.sections || [])
                    };

                    fi = updateFinancialInformation(combinedJson, fi);
                }
                setFinancialInformation(fi);

                const pageBeneficiaryInformation = applicationSummary.find((page) => page.template_page_id === "f4f489ee-3e2a-4eb2-9542-0f1da59a2d20");
                setBeneficiaryInformation(updateBeneficiaryInformation(pageBeneficiaryInformation?.json, defaultBeneficiaryInformation));

                const pagePhysicalInformation = applicationSummary.find((page) => page.template_page_id === "98dc7e3f-19cf-4ec6-80e1-7b9b922e900e");
                setPhysicalInformation(updatePhysicalInformation(pagePhysicalInformation?.json, defaultPhysicalInformation));

                const pagePoliticallyExposedPersonInformation = applicationSummary.find((page) => page.template_page_id === "96f38ebf-5a72-4d76-b0db-e56b9356df0a");
                setPoliticallyExposedPersonInformation(updatePoliticallyExposedPersonInformation(pagePoliticallyExposedPersonInformation?.json, defaultPoliticallyExposedPersonInformation));

                const pageReferralInformation = applicationSummary.find((page) => page.template_page_id === "259bcc99-1dd0-45c0-a143-9b65cf9eb7f8");
                setReferralInformation(updateReferralInformation(pageReferralInformation?.json, defaultReferralInformation));

                const pageAdditionalInsured = applicationSummary.find((page) => page.template_page_id === "9fbde5cb-9b88-47d2-aa6e-83c6c33ccf66");
                setAdditionalInsured(updateAdditionalInsured(pageAdditionalInsured?.json, defaultAdditionalInsured));
            }
        };

        if (applicationSummary) {
            loadApplicationData();
        }
    }, [applicationSummary])

    // Primero cargamos los dependientes sin inicializar su información médica
    useEffect(() => {
        const loadDependentsData = async () => {
            if (selected && applicationSummary && applicationSummary.length > 0) {
                const pageAdditionalInsured = applicationSummary.find((page) => page.template_page_id === "9fbde5cb-9b88-47d2-aa6e-83c6c33ccf66");
                if (pageAdditionalInsured && selected.product_type) {
                    try {
                        const dependentsData = await getDependents(pageAdditionalInsured, selected.product_type, quoteID, 'es');
                        setDependents(dependentsData);
                    } catch (error) {
                        console.error('Error al cargar dependientes:', error);
                    }
                }
            }
        };

        loadDependentsData();
    }, [selected, applicationSummary, quoteID]);

    // Inicializamos la información médica de los dependientes después de que 
    // tanto los dependientes como la información médica principal estén disponibles
    useEffect(() => {
        if (dependents.length > 0 && medicalInformation.size > 0) {
            const dependentsMedicalInfo = new Map<string, Map<string, ConfirmationData>>();
            for (const dependent of dependents) {
                // Por ahora solo guardamos una copia del medicalInformation para cada dependiente
                // En una implementación real, esto debería cargar datos específicos para cada dependiente
                dependentsMedicalInfo.set(dependent.id, new Map(medicalInformation));
            }
            setDependentsMedicalInformation(dependentsMedicalInfo);
        }
    }, [dependents, medicalInformation]);

    const fetchApplicationSummary = async (quoteID: string) => {
        // Intake does not dual-write legacy page blobs. The GET body is JSON null,
        // and reading .length on that crashes this screen (same guard MX already has).
        let summary: ApplicationSummary[] = [];
        try {
            const body = await getApplicationSummary(quoteID);
            summary = Array.isArray(body) ? body : [];
        } catch (error) {
            console.error('Could not read the application summary:', error);
        }
        setApplicationSummary(summary);
        if (summary.length === 0) {
            setIsLoading(false);
        }
    };

    const getPaymentSession = async (): Promise<PaymentSessionResponse> => {
        const paymentSessionRequest = createInitialPaymentSession();
        paymentSessionRequest.formData = {
            firstName,
            lastName,
            middleName : '',
            email,
            phone,
            address,
            city,
            state,
            country,
            zipCode
        };
        paymentSessionRequest.IDQuotation = quoteID;
        paymentSessionRequest.IDApplication = applicationSummary[0].quote_form_id.toString();
        paymentSessionRequest.IDPerson = generarUuid();
        paymentSessionRequest.plan = selected?.product_type ? (selected.product_type === "easy_term" ? "Easy Term" : "Master Term") : "";
        paymentSessionRequest.typePerson = typePerson === "0" ? "ID" : "Passport";
        paymentSessionRequest.frequency = selected?.frecuency_selected;
        paymentSessionRequest.amount = lastQuote?.total_premium_in_frecuency;
        paymentSessionRequest.term = Number(selected?.term_selected);
        const paymentSessionResponse = await createPaymentSession(paymentSessionRequest);
        return paymentSessionResponse;
    };

    return (
        <ConfirmationContext.Provider value={{
            isLoading, setIsLoading,
            coverageInformation, setCoverageInformation,
            personalInformationOfTheInsured, setPersonalInformationOfTheInsured,
            policyOwnerInformation, setPolicyOwnerInformation,
            policyPurposeInformation, setPolicyPurposeInformation,
            occupationInformation, setOccupationInformation,
            incomeInformation, setIncomeInformation,
            politicallyExposedPersonInformation, setPoliticallyExposedPersonInformation,
            riskyActivitiesInformation, setRiskyActivitiesInformation,
            physicalInformation, setPhysicalInformation,
            medicalInformation, setMedicalInformation,
            familyMedicalHistoryInformation, setFamilyMedicalHistoryInformation,
            insuranceInformation, setInsuranceInformation,
            substanceInformation, setSubstanceInformation,
            financialInformation, setFinancialInformation,
            beneficiaryInformation, setBeneficiaryInformation,
            referralInformation, setReferralInformation,
            additionalInsured, setAdditionalInsured,
            email, setEmail,
            backToApplication,
            backToQuoter,
            getPageIdByTemplateId: (templateId: string) => getPageIdByTemplateId(templateId, applicationSummary),
            getPaymentSession: () => getPaymentSession(),
            dependents,
            dependentsMedicalInformation
        }}>
            {children}
        </ConfirmationContext.Provider>
    );
};

export const useConfirmation = () => {
    const context = useContext(ConfirmationContext);
    if (context === undefined) {
        throw new Error('useConfirmation must be used within a ConfirmationProvider');
    }
    return context;
};




